import { makeAutoObservable } from 'mobx';
import { z } from 'zod';
import { DictionaryLike } from './Dto';
import './zodErrorMap';

/**
 * View model-helper for zod validation
 *
 * The main goal is displaying validation errors only for fields which were
 * changed and lost focus. When a fresh form is displayed it should not
 * highlight any fields as invalid. Only when user changed a value and switched
 * to another field, the previous field should be marked as invalid.
 */
export default class Validator<T> {
  private issues: readonly z.core.$ZodIssue[] = [];

  private enabledFields: Partial<Record<keyof T, boolean>> = {};
  private isAllFieldsEnabled = false;

  constructor(
    private readonly dataSource: (() => T) | T,
    private readonly schema: z.ZodType = z.looseObject({}),
  ) {
    makeAutoObservable<this, 'dataSource'>(
      this,
      { dataSource: false },
      { autoBind: true },
    );
  }

  get allErrorsAsStrings(): string[] {
    return this.issues.map((issue) => {
      // Custom (refine/superRefine) messages are shown verbatim, matching the
      // legacy joi `helper.message` behavior of not prefixing the field name.
      if (issue.code === 'custom') {
        return issue.message;
      }

      const field = camelCaseToHumanFormat(pathToFieldName(issue.path));
      return `"${field}" ${issue.message}`;
    });
  }

  get allErrors() {
    return this.collectErrors(this.issues);
  }

  get errorsForEnabledFields(): ValidatorErrors<T> {
    return this.collectErrors(this.getEnabledIssues());
  }

  get isValid(): boolean {
    return this.issues.length === 0;
  }

  validate(): boolean {
    let { dataSource } = this;
    if (typeof this.dataSource === 'function') {
      dataSource = (this.dataSource as () => T)();
    }

    const result = this.schema.safeParse(dataSource);
    this.issues = result.success ? [] : result.error.issues;
    return this.isValid;
  }

  enableFieldValidation(field: keyof T) {
    this.enabledFields[field] = true;
  }

  enableAllFieldValidation() {
    this.isAllFieldsEnabled = true;
  }

  private collectErrors(
    issues: readonly z.core.$ZodIssue[],
  ): ValidatorErrors<T> {
    const errors = {} as ValidatorErrors<T>;

    for (const issue of issues) {
      const field = pathToFieldName(issue.path) as keyof T;
      errors[field] = errors[field]
        ? `${errors[field]}. ${issue.message}`
        : issue.message;
    }

    return errors;
  }

  private getEnabledIssues(): readonly z.core.$ZodIssue[] {
    if (this.isAllFieldsEnabled) {
      return this.issues;
    }

    return this.issues.filter((issue) => {
      const field = pathToFieldName(issue.path) as keyof T;
      return this.enabledFields[field];
    });
  }
}

export type ValidatorErrors<T> = DictionaryLike<T>;

function pathToFieldName(path: ReadonlyArray<PropertyKey>): string {
  return path.map(String).join('_');
}

function camelCaseToHumanFormat(text: string): string {
  const line = text.replaceAll(
    /[A-Z]/g,
    (letter) => ` ${letter.toLowerCase()}`,
  );
  return line[0].toUpperCase() + line.slice(1);
}
